import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, X } from 'lucide-react';
import { z } from 'zod';
import { LINK_VIAS, checkLink, decideApproval } from '@mkcore/approvals/schema';
import { Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, ErrorState, PageHeader, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useActionMutation, useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { approvalQuery } from '@/features/approvals/queries';
import { DecideDialog } from '@/features/approvals/decide-dialog';
import { reasonLabel } from '@/features/approvals/reasons';
import { AgentCall, ApproveCallDialog, FormDialog, FormValues, tone, type DecideLink, type Row } from '@/features/approvals/request';

const search = z.object({
  via: z.enum(LINK_VIAS).optional(),
  exp: z.coerce.number().int().positive().optional(),
  sig: z.string().max(200).optional(),
  action: z.enum(['approve', 'reject']).optional(),
});
export const Route = createFileRoute('/_app/approvals/$id')({ validateSearch: search, component: ApprovalPage });

/**
 * One approval request (CO-0935; docs/04-domeinen/approvals.md, Beslissen via een link): where the link in a
 * notification mail, a Teams card or a Hermes chat leads. The page checks the signed link and says when it expired or
 * is not meant for you; deciding works as in the inbox, never on opening, and the history notes where it came from.
 */
function ApprovalPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const canApprove = useCan('approvals:approve');
  const q = useQuery(approvalQuery(id));
  const given = s.via && s.exp && s.sig ? { via: s.via, exp: s.exp, sig: s.sig } : null;
  const check = useQuery({ queryKey: ['approval', 'link', id, given], queryFn: () => call(checkLink, { id, ...given! }), enabled: !!given });
  const link: DecideLink | undefined = given && check.data?.status === 'valid' ? given : undefined;
  const a = q.data;
  return (
    <>
      <PageHeader title={a?.title ?? t('approvals.title')} description={a?.summary || undefined} />
      {check.data && check.data.status !== 'valid' && (
        <p role="status" className="mb-4 rounded-md bg-warning-bg p-3 text-sm text-warning">
          {t(`approvals.link.${check.data.status}`)}
        </p>
      )}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !a || (given && check.isLoading) ? (
        <Skeleton className="h-48" />
      ) : (
        <Request approval={a} link={link} canApprove={canApprove} initial={s.action} />
      )}
    </>
  );
}

function Request({ approval: a, link, canApprove, initial }: { approval: Row; link?: DecideLink; canApprove: boolean; initial?: 'approve' | 'reject' }) {
  const { t } = useTranslation();
  const pending = a.status === 'pending' && canApprove;
  const objectPath = pathOf(a.url);
  // A button on a Teams card or in a mail chooses the decision; the person still confirms it here.
  const [open, setOpen] = useState<'approve' | 'reject' | 'revise' | null>(pending ? (initial ?? null) : null);
  return (
    <Card className="grid gap-4 p-4">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-fg-3">{t('common.status')}</dt>
        <dd>
          <Badge tone={tone[a.status]}>{t(`approvals.status.${a.status}`)}</Badge>
        </dd>
        <dt className="text-fg-3">{t('approvals.requestedBy')}</dt>
        <dd>
          {a.requestedByName} {a.requestedByKind === 'agent' && <Badge tone="info">{t('approvals.agent')}</Badge>}
        </dd>
        <dt className="text-fg-3">{t('common.created')}</dt>
        <dd className="tabular">{formatDateTime(a.createdAt)}</dd>
        {a.decidedByName && (
          <>
            <dt className="text-fg-3">{t('approvals.decidedBy')}</dt>
            <dd>
              {t('approvals.decidedLine', { name: a.decidedByName, at: a.decidedAt ? formatDateTime(a.decidedAt) : '' })}
              {a.decidedVia && ` · ${t(`approvals.via.${a.decidedVia}`)}`}
            </dd>
          </>
        )}
        {a.reasonCode && (
          <>
            <dt className="text-fg-3">{t('approvals.reason')}</dt>
            <dd>{reasonLabel(t, { value: a.reasonCode, label: a.reasonCode })}</dd>
          </>
        )}
        {a.comment && (
          <>
            <dt className="text-fg-3">{t('approvals.comment')}</dt>
            <dd className="whitespace-pre-wrap">{a.comment}</dd>
          </>
        )}
      </dl>
      {a.toolCall && <AgentCall approval={a} />}
      {a.values && <FormValues approval={a} />}
      <div className="flex flex-wrap items-center gap-2">
        {pending && (
          <>
            <Button onClick={() => setOpen('approve')}>
              <Check /> {t('approvals.action.approve')}
            </Button>
            <Button variant="secondary" onClick={() => setOpen('revise')}>
              {t('approvals.action.revise')}
            </Button>
            <Button variant="ghost" onClick={() => setOpen('reject')}>
              <X /> {t('approvals.action.reject')}
            </Button>
          </>
        )}
        {!objectPath.startsWith('/approvals') && (
          <Link to={objectPath} className="text-sm text-fg-2 hover:underline">
            {t('approvals.openObject')}
          </Link>
        )}
        <Link to="/approvals" className="ml-auto text-sm text-fg-2 hover:underline">
          {t('approvals.backToInbox')}
        </Link>
      </div>
      {open === 'approve' &&
        (a.toolCall ? (
          <ApproveCallDialog approval={a} link={link} onClose={() => setOpen(null)} />
        ) : a.form ? (
          <FormDialog approval={a} link={link} onClose={() => setOpen(null)} />
        ) : (
          <ConfirmApprove approval={a} link={link} onClose={() => setOpen(null)} />
        ))}
      {(open === 'reject' || open === 'revise') && <DecideDialog open approvalId={a.id} decision={open} link={link} onClose={() => setOpen(null)} />}
    </Card>
  );
}

const pathOf = (url: string) => {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
};

/** Approving from a link asks once more: a link opens the page, it never decides by itself. */
function ConfirmApprove({ approval, link, onClose }: { approval: Row; link?: DecideLink; onClose: () => void }) {
  const { t } = useTranslation();
  const approve = useActionMutation(decideApproval, { invalidate: [['approval'], ['billing']], success: t('approvals.done.approve'), onSuccess: onClose });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('approvals.confirm.title')}</DialogTitle>
          <DialogDescription>{approval.title}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button loading={approve.isPending} onClick={() => approve.mutate({ input: { id: approval.id, decision: 'approve', ...(link && { link }) } })}>
            <Check /> {t('approvals.action.approve')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
